import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Upload, Users, MapPin } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import { api } from "../lib/api";

/**
 * Public community feed — surfaces every reader-submitted story that the
 * editorial team has approved. The submission form lives at /story.
 */
export default function CommunityPage() {
    const [stories, setStories] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/stories/approved", { params: { limit: 60 } });
                setStories(r.data || []);
            } catch {
                setStories([]);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    return (
        <div className="min-h-screen bg-black text-white">
            <SEO
                title="Community Stories"
                description="Real voices, real stories. Reader-submitted moments from the streets — curated by MyStreetzMag editors."
                ogSlug="page:community"
                keywords={["MyStreetz community", "Naija stories", "Lagos voices", "Afrobeats fans"]}
            />
            <Header />

            <main className="max-w-[1200px] mx-auto px-4 md:px-8 py-12">
                <Link
                    to="/"
                    data-testid="community-back-home"
                    className="overline text-streetz-yellow inline-flex items-center gap-2 hover:text-white mb-6"
                >
                    <ArrowLeft className="w-3 h-3" /> BACK HOME
                </Link>

                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
                    <div>
                        <div className="overline text-streetz-yellow">THE COMMUNITY</div>
                        <h1
                            className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl mt-2 flex items-center gap-3"
                            data-testid="community-page-title"
                        >
                            <Users className="w-10 h-10 text-streetz-red" />
                            Stories from the streets
                        </h1>
                        <p className="text-streetz-muted mt-3 max-w-xl">
                            Real readers. Real moments. Every piece below was submitted by someone in the
                            MyStreetz community and approved by our editorial team. Want yours next?
                        </p>
                    </div>
                    <Link
                        to="/story"
                        data-testid="community-upload-cta"
                        className="self-start inline-flex items-center gap-2 bg-streetz-red text-white px-6 py-3 overline hover:bg-white hover:text-black"
                    >
                        <Upload className="w-4 h-4" /> UPLOAD YOUR STORY
                    </Link>
                </div>

                {loading ? (
                    <p className="text-streetz-muted">Loading community stories…</p>
                ) : stories.length === 0 ? (
                    <div
                        data-testid="community-empty-state"
                        className="bg-streetz-paper border border-streetz-line p-10 text-center"
                    >
                        <Users className="w-10 h-10 text-streetz-yellow mx-auto mb-4" />
                        <h2 className="font-display uppercase text-2xl">No community stories yet</h2>
                        <p className="text-streetz-muted mt-2 max-w-md mx-auto">
                            Be the first. Tell us about a night at the club, a producer you discovered,
                            or what Afrobeats means to your block.
                        </p>
                        <Link
                            to="/story"
                            data-testid="community-empty-cta"
                            className="inline-flex items-center gap-2 mt-6 bg-streetz-yellow text-black px-6 py-3 overline hover:bg-white"
                        >
                            <Upload className="w-4 h-4" /> BE THE FIRST
                        </Link>
                    </div>
                ) : (
                    <div
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                        data-testid="community-stories-grid"
                    >
                        {stories.map((s) => (
                            <article
                                key={s.id}
                                data-testid={`community-story-${s.id}`}
                                className="bg-streetz-paper border border-streetz-line hover:border-streetz-yellow transition-colors flex flex-col"
                            >
                                {s.image_url && (
                                    <img
                                        src={s.image_url}
                                        alt={s.title}
                                        loading="lazy"
                                        className="aspect-video object-cover w-full"
                                    />
                                )}
                                <div className="p-5 flex-1 flex flex-col">
                                    <div className="flex items-center gap-2 overline text-[10px] text-streetz-muted">
                                        <span className="text-streetz-yellow">{s.name}</span>
                                        {s.location && (
                                            <>
                                                <span>·</span>
                                                <span className="inline-flex items-center gap-1">
                                                    <MapPin className="w-3 h-3" /> {s.location}
                                                </span>
                                            </>
                                        )}
                                    </div>
                                    <h3 className="font-display uppercase text-xl mt-2 leading-tight">
                                        {s.title}
                                    </h3>
                                    <p className="text-sm text-streetz-muted mt-3 line-clamp-5 whitespace-pre-line flex-1">
                                        {s.body}
                                    </p>
                                    {Array.isArray(s.tags) && s.tags.length > 0 && (
                                        <div className="flex flex-wrap gap-2 mt-4">
                                            {s.tags.slice(0, 4).map((t) => (
                                                <span
                                                    key={t}
                                                    className="overline text-[10px] text-streetz-muted border border-streetz-line px-2 py-[2px]"
                                                >
                                                    #{t}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </article>
                        ))}
                    </div>
                )}
            </main>

            <Footer />
        </div>
    );
}
